import {skipToken} from '@reduxjs/toolkit/query';
import {Action, DataStatus, showNotification} from 'platform/components';

import {useGetVehicleQuery} from '@dms/api/vehicle';
import {useRemoveWorkshopInterestVehicleMutation} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {handleApiError, VehicleWidget} from '@dms/shared';

import {buildArray, composePath, RequiredTestIdProps, suffixTestId} from 'shared/web';

interface SelectedVehicleProps extends RequiredTestIdProps {
  vehicleId: string;
  interestId: string;
}

export function SelectedVehicle(props: SelectedVehicleProps) {
  const {
    data: vehicle,
    isLoading,
    isError,
  } = useGetVehicleQuery(props.vehicleId ? {vehicleId: props.vehicleId} : skipToken);

  const [removeVehicle, {isLoading: isRemoving}] = useRemoveWorkshopInterestVehicleMutation();

  const handleRemoveVehicle = () => {
    removeVehicle({id: props.interestId})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
      })
      .catch(handleApiError);
  };

  if (!vehicle) {
    return <DataStatus isLoading={isLoading} isError={isError} minHeight={50} />;
  }

  return (
    <VehicleWidget
      isBoxed
      vehicle={{
        id: vehicle.id,
        vin: vehicle.vin,
        registrationPlate: vehicle.registrationPlate,
        make: vehicle.make,
        modelFamily: vehicle.modelFamily,
        trim: vehicle.trim,
        variant: vehicle.variant,
        vehicleType: vehicle.type?.toString(),
        firstRegistrationOn: vehicle.firstRegistrationOn,
        mileage: vehicle.mileage,
        fuelType: vehicle.fuelType,
        transmission: vehicle.transmission,
        bodyStyle: vehicle.bodyStyle,
        driveType: vehicle.drive,
        actions: buildArray<Action>()
          .add({
            type: 'button',
            title: i18n.t('entity.vehicle.labels.detail'),
            variant: 'outlined',
            leftIcon: 'action/launch',
            onClick: () => {
              window.open(composePath(vehiclesRoutes.detail, {params: {id: vehicle.id}}), '_blank');
            },
          })
          .add({
            type: 'button',
            title: i18n.t('general.actions.remove'),
            variant: 'outlined',
            leftIcon: 'action/delete',
            isLoading: isRemoving,
            onClick: handleRemoveVehicle,
          }),
      }}
      data-testid={suffixTestId('vehicleWidget', props)}
    />
  );
}
